import type { LearnTopic } from "../topic-types";

/**
 * Post-Traumatic Stress Disorder (PTSD).
 *
 * Educational, non-diagnostic content organised around understanding,
 * recognising, practising and reflecting.
 */
export const PTSD_TOPIC: LearnTopic = {
  slug: "ptsd",
  emoji: "🧠",
  title: "Post-Traumatic Stress Disorder (PTSD)",
  tagline: "Understanding PTSD and recovery",
  theme: "gold",
  category: "cptsd-responses",
  cardBlurb:
    "Understand PTSD, why symptoms can continue after trauma, how it can affect the mind and nervous system, and approaches that may support recovery.",
  isNew: true,
  seoDescription:
    "A trauma-informed, non-diagnostic learning hub about PTSD, its symptom clusters, flashbacks, triggers, the nervous system, treatment and recovery support.",
  intro: [
    "Post-Traumatic Stress Disorder (PTSD) is a recognised mental health condition that can develop after experiencing or witnessing an event involving actual or threatened death, serious injury, or sexual violence. It can affect the mind, body, sleep, relationships and sense of safety long after the immediate danger has passed.",
    "This hub offers education, not a diagnosis. Many experiences described here can have other causes, and not everyone who lives through trauma develops PTSD. Only a suitably qualified professional can assess or diagnose PTSD and discuss treatment that fits your circumstances.",
    "You are in charge of your pace. You can read one section, skip anything that feels too much, use a grounding resource, or leave and return another day. Recovery Trauma™ does not replace professional diagnosis, therapy, medical care or crisis support.",
  ],
  closing:
    "Learning about PTSD can help put language around experiences, but it cannot confirm a diagnosis or replace professional treatment. You deserve support that respects your pace, circumstances and choices. If you feel in immediate danger or unable to stay safe, use the Crisis Resources page to find urgent support.",
  continueTo: [
    { label: "Grounding Techniques", to: "/learn/grounding-techniques", note: "Present-moment practices you can adapt" },
    { label: "Understanding Your Triggers", to: "/learn/triggers" },
    { label: "Hypervigilance", to: "/learn/hypervigilance" },
    { label: "Understanding Dissociation", to: "/learn/dissociation" },
    { label: "Trauma & Sleep", to: "/learn/trauma-sleep" },
    { label: "Emotional Flashbacks", to: "/learn/emotional-flashbacks" },
    { label: "Window of Tolerance", to: "/learn/window-of-tolerance" },
    { label: "Breathing", to: "/breathing", note: "Optional guided regulation practices" },
    { label: "Somatic Practices", to: "/somatic" },
    { label: "Self-Assessments", to: "/learn/assessments", note: "Educational reflections, never diagnoses" },
    { label: "Complex PTSD (C-PTSD)", to: "/learn/cptsd" },
    { label: "Crisis Resources", to: "/crisis" },
  ],
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What PTSD is, how it can develop, and how clinicians describe its main features.",
      intro:
        "You can begin with information only. There is no need to revisit details of anything that happened to you in order to learn about PTSD.",
      resources: [
        {
          slug: "what-is-ptsd",
          kind: "learn",
          title: "What PTSD is and how it can develop",
          summary: "A plain-English introduction to PTSD and why responses can continue after danger has passed.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "PTSD can develop after a person experiences or witnesses trauma. Examples can include assault, abuse, serious accidents, war, disasters, traumatic loss, or medical events. Repeated exposure through work may also affect some people. An event does not have to fit someone else's idea of what is 'bad enough' for its impact to matter.",
            },
            {
              type: "prose",
              text: "During danger, the brain and body organise around survival. Afterwards, memories, alarm responses and protective habits may not settle in the usual way. The nervous system can react to reminders as though the threat is happening now, even when another part of the person knows they are in the present.",
            },
            {
              type: "list",
              title: "PTSD is not",
              items: [
                "A sign of weakness, failure or lack of resilience",
                "An inevitable response to trauma — many people do not develop it",
                "Something a person chooses or can simply switch off",
                "A conclusion that can be reached from an online list alone",
              ],
            },
            {
              type: "note",
              text: "This resource is educational and non-diagnostic. A GP, psychiatrist, clinical psychologist or another appropriately qualified professional can assess symptoms in their full context.",
            },
          ],
        },
        {
          slug: "four-symptom-clusters",
          kind: "learn",
          title: "The four main PTSD symptom clusters",
          summary: "Intrusion, avoidance, changes in thoughts and mood, and hyperarousal or reactivity.",
          minutes: 7,
          blocks: [
            {
              type: "prose",
              text: "Clinical guidance commonly groups PTSD symptoms into four clusters. These groupings help professionals understand a pattern; they are not a self-diagnosis checklist. Experiences vary in form, intensity and frequency.",
            },
            {
              type: "list",
              title: "The four clusters",
              items: [
                "Intrusion or re-experiencing — unwanted memories, nightmares, flashbacks, or strong emotional and physical reactions to reminders",
                "Avoidance — trying not to think or feel about the event, or avoiding people, places, conversations and activities linked with it",
                "Changes in thoughts and mood — numbness, guilt, shame, disconnection, difficulty recalling parts of the event, or persistent negative beliefs",
                "Hyperarousal and reactivity — feeling on guard, startling easily, irritability, sleep or concentration difficulties, and sometimes risky or impulsive behaviour",
              ],
            },
            {
              type: "note",
              text: "Some people recognise only part of this picture, or have symptoms linked to another condition or circumstance. A professional assessment looks at duration, impact, context and possible alternatives rather than counting isolated symptoms.",
            },
          ],
        },
        {
          slug: "nervous-system-after-trauma",
          kind: "learn",
          title: "How trauma can affect the nervous system",
          summary: "Why the body's alarm can remain sensitive after the event is over.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "The autonomic nervous system helps mobilise us towards action and supports rest and recovery. After trauma, its threat-detection system may become more sensitive. A sound, expression, smell, sensation or situation can prompt activation before conscious thought catches up.",
            },
            {
              type: "list",
              title: "Protective states may include",
              items: [
                "Mobilisation — racing heart, tense muscles, rapid breathing, anger, panic, or an urge to escape",
                "Heightened monitoring — scanning surroundings, checking exits, or tracking other people's moods",
                "Shutdown or disconnection — heaviness, numbness, fogginess, reduced speech, or feeling far away",
                "Moving between these states as the nervous system responds to different cues",
              ],
            },
            {
              type: "prose",
              text: "These responses are protective, not evidence that a person is broken. Recovery may include helping the body notice present-day safety, gradually increasing capacity and receiving appropriate professional support.",
            },
            {
              type: "cta",
              text: "Explore a visual guide to activation, shutdown and a workable middle zone.",
              label: "Open Window of Tolerance",
              to: "/learn/window-of-tolerance",
            },
          ],
        },
        {
          slug: "ptsd-comparisons",
          kind: "learn",
          title: "PTSD, C-PTSD and responses after trauma",
          summary: "Important differences without ranking or minimising anyone's experience.",
          minutes: 7,
          blocks: [
            {
              type: "prose",
              text: "Fear, disrupted sleep, intrusive memories, feeling on edge and avoiding reminders can be understandable responses in the days and weeks after trauma. For many people these ease with time, safety and support. PTSD involves a particular pattern that persists and causes significant distress or disruption; assessment also considers when symptoms began and how long they have continued.",
            },
            {
              type: "note",
              text: "There is no required way to feel after trauma. Feeling distressed does not automatically mean PTSD, and appearing calm does not mean an experience had no impact. Early support is valid even before anyone knows whether symptoms will continue.",
            },
            {
              type: "prose",
              text: "PTSD and C-PTSD both involve responses to trauma. In ICD-11, both include re-experiencing, avoidance and a sense of current threat. C-PTSD also includes persistent difficulties with emotional regulation, self-concept and relationships, and is often associated with repeated or prolonged trauma from which escape was difficult. Diagnostic systems describe the conditions somewhat differently, so a clinician considers the whole person rather than one comparison table.",
            },
            {
              type: "list",
              title: "Worth remembering",
              items: [
                "PTSD can follow one event or repeated events",
                "C-PTSD is not a ranking that means one person's suffering is more valid",
                "People may have overlapping experiences without meeting criteria for either condition",
                "Only a qualified professional can assess or diagnose either condition",
              ],
            },
            {
              type: "cta",
              text: "Learn more about the wider effects associated with prolonged or repeated trauma.",
              label: "Open the C-PTSD hub",
              to: "/learn/cptsd",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognise",
      blurb: "Common PTSD experiences, described gently and without turning them into a checklist.",
      intro:
        "You may recognise some, all or none of these experiences. Noticing is optional, and recognising something here does not establish a diagnosis.",
      resources: [
        {
          slug: "flashbacks-and-intrusive-memories",
          kind: "learn",
          title: "Flashbacks and intrusive memories",
          summary: "How the past can arrive as images, sensations, emotions or a sense that it is happening now.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "Intrusive memories are unwanted recollections that enter awareness without invitation. A flashback can involve vivid images, but it may also arrive through sound, body sensation, emotion, smell or a powerful sense of being back in the event. The experience can be brief or prolonged and can vary between people.",
            },
            {
              type: "list",
              title: "In the moment, it may help to orient to now",
              items: [
                "Name where you are and today's date, if that feels grounding",
                "Look for several neutral objects and describe their colour or shape",
                "Notice contact with the floor, chair or another steady surface",
                "Remind yourself: 'This is a memory or alarm response; I am noticing the present now'",
              ],
            },
            {
              type: "note",
              text: "If focusing on the body or breath increases distress, you can stop and choose an external focus instead. There is no single grounding method that suits everyone.",
            },
            {
              type: "cta",
              text: "Read more about intense memory and emotion states.",
              label: "Explore Emotional Flashbacks",
              to: "/learn/emotional-flashbacks",
            },
          ],
        },
        {
          slug: "triggers-hypervigilance-startle",
          kind: "learn",
          title: "Triggers, hypervigilance and startle response",
          summary: "When reminders activate alarm, scanning or a strong reflexive response.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "A trigger is a present-day cue associated with past danger. It might be obvious, subtle or outside conscious awareness. Hypervigilance is persistent watchfulness for threat; an increased startle response means sudden sound, touch or movement may produce a strong involuntary reaction.",
            },
            {
              type: "list",
              title: "This can include",
              items: [
                "Checking exits, locks, messages or other people's expressions repeatedly",
                "Finding it difficult to concentrate because attention keeps scanning for risk",
                "Jumping, freezing or becoming activated at sudden noises or movements",
                "Feeling exhausted after long periods of alertness",
              ],
            },
            {
              type: "cta",
              text: "Explore cues, patterns and compassionate ways to respond.",
              label: "Open Understanding Your Triggers",
              to: "/learn/triggers",
            },
            {
              type: "cta",
              text: "Learn more about a nervous system that stays on guard.",
              label: "Open Hypervigilance",
              to: "/learn/hypervigilance",
            },
          ],
        },
        {
          slug: "avoidance-and-emotional-numbing",
          kind: "learn",
          title: "Avoidance and emotional numbing",
          summary: "Protective distance from reminders, feelings, people or activities.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Avoidance can reduce distress in the short term by creating distance from reminders, conversations, places, thoughts or feelings. Emotional numbing can feel like disconnection from painful emotions, but also from pleasure, closeness or interest. Both may have begun as ways to cope with more than the system could hold.",
            },
            {
              type: "list",
              title: "People may notice",
              items: [
                "Changing routines to avoid reminders or situations that feel risky",
                "Staying constantly busy to keep thoughts or feelings away",
                "Using alcohol, substances, food, work or screens to create distance",
                "Feeling detached from people or activities that once mattered",
                "Difficulty imagining a future or accessing positive emotion",
              ],
            },
            {
              type: "note",
              text: "Avoidance is not laziness and numbness is not a lack of care. Forcing exposure without safety, consent and suitable support can be unhelpful. A qualified trauma-informed professional can help plan gradual work when appropriate.",
            },
          ],
        },
        {
          slug: "sleep-nightmares-and-dissociation",
          kind: "learn",
          title: "Sleep, nightmares and dissociation",
          summary: "Why rest and feeling present can become difficult after trauma.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "Sleep can feel vulnerable when the nervous system is monitoring for danger. Difficulty falling asleep, waking often, nightmares and feeling unrested can reinforce daytime alertness. Dissociation is a protective disconnection that can affect awareness, memory, identity, emotion, the body or surroundings.",
            },
            {
              type: "list",
              title: "Dissociation may be described as",
              items: [
                "Feeling unreal, far away, foggy or on autopilot",
                "Feeling that surroundings are dreamlike or unfamiliar",
                "Numbness or reduced awareness of body sensations",
                "Gaps in memory that deserve professional attention, especially when frequent or concerning",
              ],
            },
            {
              type: "cta",
              text: "Explore trauma-informed information about rest and nighttime safety.",
              label: "Open Trauma & Sleep",
              to: "/learn/trauma-sleep",
            },
            {
              type: "cta",
              text: "Learn about protective disconnection and gentle reorientation.",
              label: "Open Understanding Dissociation",
              to: "/learn/dissociation",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practise",
      blurb: "Optional ways to support present-moment safety and prepare for professional support.",
      intro:
        "These practices are invitations, not treatment. You can adapt them, keep your eyes open, choose an external focus, pause, or stop completely.",
      resources: [
        {
          slug: "grounding-and-regulation-options",
          kind: "practice",
          title: "Grounding and regulation options",
          summary: "Choose a present-focused practice rather than pushing through overwhelm.",
          minutes: 8,
          blocks: [
            {
              type: "steps",
              title: "A choice-led sequence",
              items: [
                { label: "Check the present", note: "Ask whether you are physically safe enough to pause. If not, move towards practical or urgent support." },
                { label: "Choose your focus", note: "Look outward at neutral objects, notice steady physical contact, or use breath only if breath focus feels supportive." },
                { label: "Use a small dose", note: "Try ten to thirty seconds. More is not automatically better." },
                { label: "Check again", note: "Continue, change method or stop according to what you notice." },
                { label: "Connect if useful", note: "Contact someone safe or a professional rather than managing alone when support would help." },
              ],
            },
            {
              type: "cta",
              text: "Browse sensory and orienting practices for returning to the present.",
              label: "Open Grounding Techniques",
              to: "/learn/grounding-techniques",
            },
            {
              type: "cta",
              text: "Try guided breathing only if focusing on breath feels comfortable today.",
              label: "Open Breathing",
              to: "/breathing",
            },
            {
              type: "cta",
              text: "Explore body-based practices with choice and pacing.",
              label: "Open Somatic Practices",
              to: "/somatic",
            },
          ],
        },
        {
          slug: "treatment-and-recovery-approaches",
          kind: "learn",
          title: "Treatment and recovery approaches",
          summary: "Evidence-informed options and the importance of fit, consent and pacing.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "PTSD is treatable, and many people experience meaningful improvement. Clinical guidelines commonly recommend trauma-focused psychological therapies, while the right approach depends on the person's needs, health, preferences, safety and access to care.",
            },
            {
              type: "list",
              title: "Approaches a qualified professional may discuss",
              items: [
                "Trauma-focused cognitive behavioural therapies, including Cognitive Processing Therapy and prolonged exposure approaches",
                "Eye Movement Desensitisation and Reprocessing (EMDR)",
                "Narrative or other trauma-focused therapies recommended in relevant clinical guidance",
                "Medication for some adults, discussed with a prescriber who can explain expected benefits, side effects and alternatives",
                "Support for sleep, physical health, substance use, relationships and practical safety alongside trauma treatment",
              ],
            },
            {
              type: "prose",
              text: "Good care should include informed choice. You can ask what an approach involves, what evidence supports it, how pacing and stabilisation are handled, what happens if you become overwhelmed, and what alternatives exist. You can also ask for accessibility, cultural or communication needs to be respected.",
            },
            {
              type: "note",
              text: "Recovery is not a promise, deadline or straight line. Skills and educational resources from Recovery Trauma™ may complement care, but they do not provide trauma processing, diagnosis or clinical treatment.",
            },
          ],
        },
        {
          slug: "seeking-professional-support",
          kind: "learn",
          title: "When and how to seek professional support",
          summary: "Preparing for a conversation with a GP or trauma-informed professional.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "You do not need to be certain that you have PTSD before seeking help. Support may be useful when symptoms persist, interfere with sleep, work, relationships or daily life, lead to increased substance use, or simply feel difficult to carry alone.",
            },
            {
              type: "steps",
              title: "Preparing for an appointment",
              items: [
                { label: "Write down the impact", note: "You can describe sleep, concentration, avoidance, memories or body responses without sharing detailed trauma history before you are ready." },
                { label: "Mention time and change", note: "Note roughly when experiences began, how often they occur and what has changed in daily life." },
                { label: "Ask about assessment", note: "A GP or mental health professional can discuss possible causes, referral options and treatment choices." },
                { label: "Take support", note: "You may be able to bring someone you trust, request an interpreter or ask for communication adjustments." },
                { label: "Ask questions", note: "You are allowed to ask about confidentiality, records, waiting times, costs, clinician experience and what happens next." },
              ],
            },
            {
              type: "note",
              text: "If you are in immediate danger, feel unable to keep yourself or someone else safe, or need urgent medical help, use local emergency services or the Crisis Resources page rather than waiting for a routine appointment.",
            },
            {
              type: "cta",
              text: "Find urgent and crisis support options by location.",
              label: "Open Crisis Resources",
              to: "/crisis",
            },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Private, optional prompts for noticing impact, needs and next steps.",
      intro:
        "Reflection is not required for learning. Avoid writing trauma details if that feels destabilising; you can focus on current needs and supportive next steps instead.",
      resources: [
        {
          slug: "what-supports-me-now",
          kind: "reflect",
          title: "What supports me now?",
          summary: "A present-focused reflection on patterns, choice and support.",
          minutes: 8,
          blocks: [
            {
              type: "note",
              text: "These prompts cannot assess PTSD. Skip any question, stop at any time, and seek support if reflection begins to feel unmanageable.",
            },
            {
              type: "prompts",
              title: "Notice without judgement",
              items: [
                "What signs tell me my sense of threat is rising?",
                "Which present-focused actions help even slightly, and which ones do not suit me?",
                "Are there situations I avoid that are affecting something important to me?",
                "What helps rest feel a little safer or more possible?",
                "What would I want a professional to understand about the impact on my daily life?",
                "Who could support me with one next step, if I choose to take it?",
              ],
            },
            {
              type: "cta",
              text: "Use short, non-diagnostic reflections to explore related patterns.",
              label: "Browse Self-Assessments",
              to: "/learn/assessments",
            },
          ],
        },
        {
          slug: "my-support-conversation-notes",
          kind: "worksheet",
          title: "My support conversation notes",
          summary: "A printable prompt sheet for a professional support conversation.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "You can use this sheet to organise what you want to say without recording details you would rather keep private. It is not a screening or diagnostic form.",
            },
          ],
          worksheetFields: [
            { label: "What has been affecting me recently", hint: "Symptoms, daily impact or concerns — not trauma details unless you choose", lines: 4 },
            { label: "When I first noticed this and how often it happens", lines: 3 },
            { label: "What seems to make things harder", lines: 3 },
            { label: "What helps, even a little", lines: 3 },
            { label: "Questions I want to ask", lines: 4 },
            { label: "Adjustments or support I need during the conversation", lines: 3 },
          ],
        },
      ],
    },
  ],
};